import { Holding, valuationStatusOf } from "../../../../../services/holdings";
import {
  isAvailable,
  isOverrideRecoverable,
  isSelfResolvingValuation,
  valuationDetailFor,
  valuationMessageFor,
  ValuationStatus,
  valuationWarningLeadFor,
  valuationWarningText,
} from "../../../../../services/valuation";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { InvestmentsDetails } from "../../../types";
import { accountSnapshotOf, isLinkedSettlement } from "../../../utils";
import { LedgerCaveat } from "./types";

const NAMED_SYMBOL_LIMIT = 3;

const consequenceOf = (hasRepairable: boolean): string =>
  `Broker value, net gain and allocation stay unavailable until ${
    hasRepairable ? "a price is set" : "it's resolved"
  }.`;

const ACCOUNT_CONSEQUENCE =
  "Net gain and allocation stay unavailable until it's resolved.";

const symbolListOf = (holdings: Holding[]): string => {
  const symbols = holdings.map((holding) => holding.instrument.symbol);
  const named = symbols.slice(0, NAMED_SYMBOL_LIMIT);
  const overflow = symbols.length - named.length;
  if (overflow > 0) {
    return `${named.join(", ")} and ${overflow} more`;
  }
  return named.length === 1
    ? named[0]
    : `${named.slice(0, -1).join(", ")} and ${named[named.length - 1]}`;
};

const singleDetailOf = (holding: Holding): string => {
  const status = valuationStatusOf(holding);
  const asOf = status.asOf
    ? ` as of ${formatDate(status.asOf, DateFormat.DMY_SPACE)}`
    : "";
  return `${holding.instrument.symbol}: ${valuationMessageFor(status)}${asOf}.`;
};

const valuationCaveatOf = (unpriced: Holding[]): LedgerCaveat => {
  const repairable = unpriced.filter((holding) =>
    isOverrideRecoverable(valuationStatusOf(holding)),
  );
  const isSingle = unpriced.length === 1;
  const lead = isSingle
    ? "One valuation needs attention."
    : `${unpriced.length} valuations need attention.`;
  const subject = isSingle
    ? singleDetailOf(unpriced[0])
    : `${symbolListOf(unpriced)} have no usable price.`;
  return {
    key: "valuation",
    lead,
    detail: `${subject} ${consequenceOf(repairable.length > 0)}`,
    ...(repairable.length > 0 && { repairable }),
  };
};

// The header's headline already names the short reason, so only a longer explanation is worth repeating here.
const accountDetailOf = (
  valuation: ValuationStatus | null | undefined,
): string => {
  const detail = valuationDetailFor(valuation);
  return detail === valuationMessageFor(valuation)
    ? ACCOUNT_CONSEQUENCE
    : detail;
};

const needsAttention = (holding: Holding): boolean => {
  const status = valuationStatusOf(holding);
  return !isAvailable(status) && !isSelfResolvingValuation(status);
};

export const ledgerCaveatsOf = (
  investment: InvestmentsDetails,
  holdings: Holding[] | undefined,
): LedgerCaveat[] => {
  const caveats: LedgerCaveat[] = [];
  const unpriced = (holdings ?? []).filter(
    (holding) => !holding.isClosed && needsAttention(holding),
  );

  if (unpriced.length > 0) {
    caveats.push(valuationCaveatOf(unpriced));
  } else if (
    !accountSnapshotOf(investment).isPriced &&
    !isSelfResolvingValuation(investment.valuation)
  ) {
    // Holdings still loading or failed can't name a position, but the account's own reason still can.
    caveats.push({
      key: "account-valuation",
      lead: "Broker value is unavailable.",
      detail: accountDetailOf(investment.valuation),
    });
  }

  const cashBalance = investment.cashBalance ?? null;
  if (
    !isLinkedSettlement(investment) &&
    cashBalance !== null &&
    cashBalance < 0
  ) {
    caveats.push({
      key: "negative-cash",
      lead: "Cash balance is negative.",
      detail: formatCurrency(cashBalance),
    });
  }

  for (const warning of investment.valuationWarnings ?? []) {
    caveats.push({
      key: `${warning.instrumentId}-${warning.code}`,
      lead: `${valuationWarningLeadFor(warning)}.`,
      detail: `${valuationWarningText(warning)}.`,
    });
  }

  return caveats;
};
